/**
 * Pure H OS · Sprint 3 · Creative Review (grid + drawer).
 * Composes Card, SectionHeader, Button, SeverityBadge, Drawer, TabBar.
 */
import { useMemo, useState } from "react";
import { CheckCircle2, XCircle, Clock3, Sparkles } from "lucide-react";
import { Card, SectionHeader, Button } from "../../components/lib";
import { Drawer, TabBar, Field } from "../../components/lib.overlay";
import { CREATIVES, CreativeAsset, CreativeStatus, CAMPAIGNS } from "../../mocks/marketingData";

const STATUS_META: Record<CreativeStatus, { fg: string; bg: string; label: string; icon: any }> = {
  "pending":            { fg: "var(--ph-warning)", bg: "var(--ph-warning-tint)", label: "Pending",  icon: Clock3        },
  "approved":           { fg: "var(--ph-success)", bg: "var(--ph-success-tint)", label: "Approved", icon: CheckCircle2  },
  "changes-requested":  { fg: "var(--ph-danger)",  bg: "var(--ph-danger-tint)",  label: "Changes",  icon: XCircle       },
};

export default function CreativeReview() {
  const [tab, setTab] = useState<CreativeStatus | "all">("pending");
  const [selected, setSelected] = useState<CreativeAsset | null>(null);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: CREATIVES.length };
    CREATIVES.forEach((cr) => { c[cr.status] = (c[cr.status] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(
    () => tab === "all" ? CREATIVES : CREATIVES.filter((c) => c.status === tab),
    [tab],
  );

  return (
    <div className="space-y-6" data-testid="mkt-creative">
      <TabBar<CreativeStatus | "all">
        testId="mkt-creative-tabs"
        active={tab}
        onChange={setTab}
        tabs={[
          { key: "pending",           label: "Pending",  count: counts.pending           },
          { key: "changes-requested", label: "Changes",  count: counts["changes-requested"] },
          { key: "approved",          label: "Approved", count: counts.approved          },
          { key: "all",               label: "All",      count: counts.all               },
        ]}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mkt-creative-grid">
        {filtered.map((c) => {
          const m = STATUS_META[c.status];
          const Icon = m.icon;
          return (
            <button
              key={c.id}
              data-testid={`mkt-creative-card-${c.id}`}
              onClick={() => setSelected(c)}
              className="text-left transition-transform"
              style={{
                background: "var(--ph-surface-card)",
                border: "1px solid var(--ph-border-subtle)",
                borderRadius: "var(--ph-radius-md)",
                boxShadow: "var(--ph-shadow-card)",
                overflow: "hidden",
              }}
            >
              <div style={{ background: c.thumb, height: 160 }} className="relative">
                <div
                  className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase"
                  style={{
                    background: m.bg, color: m.fg,
                    letterSpacing: "0.14em", borderRadius: "var(--ph-radius-sm)",
                  }}
                >
                  <Icon size={11} /> {m.label}
                </div>
                <div
                  className="absolute bottom-2 right-2 text-[10px] font-semibold px-1.5 py-0.5"
                  style={{ background: "rgba(255,255,255,0.9)", color: "var(--ph-ink-strong)", borderRadius: "var(--ph-radius-sm)" }}
                >
                  {c.format}
                </div>
              </div>
              <div className="p-3">
                <div className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>{c.title}</div>
                <div className="text-[11px] mt-0.5" style={{ color: "var(--ph-ink-muted)" }}>
                  v{c.version} · reviewed by {c.reviewer} · {c.reviewedRelative}
                </div>
                {typeof c.aiScore === "number" && (
                  <div className="mt-2 flex items-center gap-2 text-[10px]" style={{ color: "var(--ph-ai)" }}>
                    <Sparkles size={10} />
                    <div className="flex-1 h-1 rounded" style={{ background: "var(--ph-border-subtle)", overflow: "hidden" }}>
                      <div style={{ width: `${c.aiScore}%`, height: "100%", background: "var(--ph-ai)" }} />
                    </div>
                    <span className="tabular-nums">{c.aiScore}</span>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Review" testId="mkt-creative-drawer" width={480}>
        {selected && (
          <div>
            <div style={{ background: selected.thumb, height: 200, borderRadius: "var(--ph-radius-md)" }} className="mb-4" />
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>
              {CAMPAIGNS.find((c) => c.id === selected.campaignId)?.name}
            </div>
            <div className="text-lg font-semibold mt-1 mb-3" style={{ color: "var(--ph-ink-strong)", fontFamily: "var(--ph-display-font)" }}>
              {selected.title}
            </div>
            <Field label="Status" inline>
              <span className="px-2 py-0.5 text-[10px] font-semibold uppercase"
                style={{ background: STATUS_META[selected.status].bg, color: STATUS_META[selected.status].fg, letterSpacing: "0.14em", borderRadius: "var(--ph-radius-sm)" }}>
                {STATUS_META[selected.status].label}
              </span>
            </Field>
            <Field label="Format" inline><span className="text-sm">{selected.format}</span></Field>
            <Field label="Version" inline><span className="text-sm tabular-nums">v{selected.version}</span></Field>
            <Field label="Reviewer" inline><span className="text-sm">{selected.reviewer}</span></Field>
            {typeof selected.aiScore === "number" && (
              <Field label="AI Score" hint="Estimated brand-guideline adherence + attention prediction." inline>
                <span className="inline-flex items-center gap-1 text-sm tabular-nums" style={{ color: "var(--ph-ai)" }}>
                  <Sparkles size={12} /> {selected.aiScore}/100
                </span>
              </Field>
            )}
            <div className="pt-3">
              <div className="text-[10px] uppercase tracking-[0.14em] mb-1.5" style={{ color: "var(--ph-ink-muted)" }}>Reviewer notes</div>
              <div className="text-sm p-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)", color: "var(--ph-ink-default)" }}>
                {selected.notes}
              </div>
            </div>
            <div className="pt-4 flex justify-end gap-2">
              <Button variant="secondary" testId="mkt-cr-request-changes">Request changes</Button>
              <Button testId="mkt-cr-approve">Approve</Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
